import React, { useMemo, useState } from "react";
import { Box, Typography, Popover, TextField, InputAdornment, alpha } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckIcon from "@mui/icons-material/Check";
import SearchIcon from "@mui/icons-material/Search";

const TRIGGER_SX = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
  width: "100%",
  minHeight: 32,
  height: 32,
  px: 1.25,
  fontSize: "0.8125rem",
  lineHeight: 1.25,
  color: "#0f172a",
  backgroundColor: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "6px",
  cursor: "pointer",
  transition: "border-color 0.15s ease, box-shadow 0.15s ease",
  "&:hover:not(.is-disabled)": {
    borderColor: "#cbd5e1",
    backgroundColor: "#fafbfc",
  },
  "&:focus-visible": {
    outline: "none",
    borderColor: "#3b82f6",
    boxShadow: "0 0 0 2px rgba(59, 130, 246, 0.18)",
  },
  "&.is-disabled": {
    opacity: 0.55,
    cursor: "not-allowed",
    backgroundColor: "#f8fafc",
  },
  "&.is-open": {
    borderColor: "#3b82f6",
    boxShadow: "0 0 0 2px rgba(59, 130, 246, 0.18)",
  },
};

const AppliesToRuleTypesSelect = ({
  options = [],
  value = [],
  onChange,
  disabled = false,
  id,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [search, setSearch] = useState("");
  const open = Boolean(anchorEl);

  const selectedSet = useMemo(() => new Set(value || []), [value]);

  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (opt) =>
        String(opt.label || "").toLowerCase().includes(q) ||
        String(opt.value || "").toLowerCase().includes(q)
    );
  }, [options, search]);

  const selectedLabels = useMemo(
    () =>
      (value || [])
        .map((v) => options.find((o) => o.value === v)?.label || v)
        .filter(Boolean),
    [value, options]
  );

  const triggerLabel = useMemo(() => {
    if (selectedLabels.length === 0) return null;
    if (selectedLabels.length === 1) return selectedLabels[0];
    if (selectedLabels.length === 2) return selectedLabels.join(", ");
    return `${selectedLabels[0]}, ${selectedLabels[1]} +${selectedLabels.length - 2}`;
  }, [selectedLabels]);

  const toggleValue = (optionValue) => {
    const next = new Set(value || []);
    if (next.has(optionValue)) next.delete(optionValue);
    else next.add(optionValue);
    onChange(Array.from(next));
  };

  const selectAll = () => onChange(options.map((o) => o.value));
  const clearAll = () => onChange([]);

  const handleClose = () => {
    setAnchorEl(null);
    setSearch("");
  };

  return (
    <>
      <Box
        component="button"
        type="button"
        id={id}
        disabled={disabled}
        className={[open ? "is-open" : "", disabled ? "is-disabled" : ""].filter(Boolean).join(" ")}
        onClick={(e) => {
          if (disabled) return;
          setAnchorEl(e.currentTarget);
        }}
        sx={{
          ...TRIGGER_SX,
          fontFamily: "inherit",
          textAlign: "left",
          appearance: "none",
          WebkitAppearance: "none",
          margin: 0,
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Typography
          component="span"
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontSize: "inherit",
            color: triggerLabel ? "#0f172a" : "#94a3b8",
          }}
        >
          {triggerLabel || "Select rule types"}
        </Typography>
        <ArrowDropDownIcon
          sx={{
            fontSize: 20,
            color: "#94a3b8",
            flexShrink: 0,
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 0.15s ease",
          }}
        />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              width: anchorEl?.offsetWidth ? Math.max(anchorEl.offsetWidth, 280) : 280,
              maxWidth: 360,
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
              boxShadow:
                "0 4px 6px -1px rgba(15, 23, 42, 0.08), 0 2px 4px -2px rgba(15, 23, 42, 0.06)",
              overflow: "hidden",
            },
          },
        }}
      >
        <Box sx={{ p: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
          <TextField
            size="small"
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
            autoFocus
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: "0.8125rem",
                height: 32,
                borderRadius: "6px",
                backgroundColor: "#f8fafc",
                "& fieldset": { borderColor: "#e2e8f0" },
                "&:hover fieldset": { borderColor: "#cbd5e1" },
                "&.Mui-focused fieldset": { borderColor: "#3b82f6", borderWidth: 1 },
              },
              "& .MuiInputBase-input": { py: 0.5, px: 0.5 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 0 }}>
                  <SearchIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 0.5,
              pb: 0.25,
            }}
          >
            <Typography
              component="button"
              type="button"
              onClick={selectAll}
              sx={{
                border: 0,
                background: "none",
                p: 0,
                fontSize: "0.75rem",
                fontWeight: 500,
                color: "#3b82f6",
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              Select all
            </Typography>
            <Typography
              component="button"
              type="button"
              onClick={clearAll}
              sx={{
                border: 0,
                background: "none",
                p: 0,
                fontSize: "0.75rem",
                fontWeight: 500,
                color: "#64748b",
                cursor: "pointer",
                "&:hover": { color: "#0f172a", textDecoration: "underline" },
              }}
            >
              Clear
            </Typography>
          </Box>

          <Box
            role="listbox"
            sx={{
              maxHeight: 220,
              overflowY: "auto",
              mx: -0.25,
              "&::-webkit-scrollbar": { width: 6 },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: alpha("#94a3b8", 0.35),
                borderRadius: 3,
              },
            }}
          >
            {filteredOptions.length === 0 ? (
              <Typography sx={{ px: 1.5, py: 1, fontSize: "0.8125rem", color: "#94a3b8" }}>
                No matches
              </Typography>
            ) : (
              filteredOptions.map((opt) => {
                const checked = selectedSet.has(opt.value);
                return (
                  <Box
                    key={opt.value}
                    role="option"
                    aria-selected={checked}
                    onClick={() => toggleValue(opt.value)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      px: 1,
                      py: 0.625,
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "0.8125rem",
                      color: "#0f172a",
                      userSelect: "none",
                      "&:hover": { backgroundColor: "#f1f5f9" },
                      ...(checked && { backgroundColor: alpha("#3b82f6", 0.06) }),
                    }}
                  >
                    <Typography sx={{ fontSize: "inherit", lineHeight: 1.35 }}>{opt.label}</Typography>
                    <CheckIcon
                      sx={{
                        fontSize: 16,
                        color: checked ? "#3b82f6" : "transparent",
                        flexShrink: 0,
                      }}
                    />
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default AppliesToRuleTypesSelect;
